import { createTheme, alpha } from "@mui/material/styles";

// Custom severity accent scale, matching the runtime notification contract's five severity
// levels (info/warning/medium/high/critical). Kept separate from the semantic intents above
// because notification stripes/chips need mutually-distinguishable hues at a glance, whereas
// the semantic intents intentionally group some of those levels together (see StatusBadge).
// `light` variants are tuned to clear 4.5:1 text contrast when rendered as chip labels on top
// of their own alpha(main, 0.15) tinted fill (see getSeverityAccentColor).
interface SeverityColor {
  main: string;
  light: string;
}
interface SeverityPalette {
  critical: SeverityColor;
  high: SeverityColor;
  medium: SeverityColor;
  warning: SeverityColor;
  info: SeverityColor;
}

declare module "@mui/material/styles" {
  interface Palette {
    severity: SeverityPalette;
  }
  interface PaletteOptions {
    severity: SeverityPalette;
  }
}

export const theme = createTheme({
  cssVariables: true,
  palette: {
    mode: "dark",
    primary: {
      // Situate brand red — CTAs, active nav indicator, destructive accents.
      // Darkened from the raw brand red (#ef3d24) so white button text clears the 4.5:1
      // AA text-contrast threshold (raw brand red only reaches ~3.9:1 with white text).
      main: "#d73720",
      // Brighter tint for use as *text* on dark/tinted surfaces (e.g. badge labels),
      // where the darker `main` would be too close in luminance to read clearly.
      light: "#e37363",
      contrastText: "#ffffff",
    },
    secondary: {
      // Teal — brand accent, live indicators, secondary telemetry. Kept clearly distinct
      // in hue from `success` (green) so the two never get confused in charts/badges.
      main: "#21b8a6",
    },
    background: {
      // Deep navy base — full-screen console backdrop.
      default: "#041c39",
      // Slightly lighter navy — sidebar, card/panel surfaces.
      paper: "#08254a",
    },
    text: {
      primary: "#f4f7fb",
      secondary: "#8493aa",
    },
    error: {
      // Critical severity.
      main: "#f05252",
      light: "#f47d7d",
    },
    warning: {
      // High / medium severity accent.
      main: "#d89519",
      light: "#e2b052",
    },
    success: {
      // Positive / active / synced state. Tuned (not just for hue separation from
      // `secondary`, but for color-vision-deficiency safety) against `error.main`: the
      // original green (#22c55e) collapsed to near-indistinguishable from error red under
      // simulated deuteranopia (the most common CVD, ~5% of males). This darker, slightly
      // more desaturated green keeps a clear luminance/hue gap from red under protanopia,
      // deuteranopia, and tritanopia simulations, while staying ~29° from secondary's teal.
      main: "#209750",
      light: "#58b17c",
    },
    info: {
      // Low severity / secondary telemetry blue.
      main: "#5b83c2",
      light: "#84a2d1",
    },
    // Custom severity accent scale — see SeverityPalette above.
    severity: {
      critical: { main: "#d95763", light: "#e17982" },
      high: { main: "#ff7b29", light: "#ff7b29" },
      medium: { main: "#e8c547", light: "#e8c547" },
      warning: { main: "#d89519", light: "#e2b052" },
      info: { main: "#5b83c2", light: "#84a2d1" },
    },
    divider: alpha("#d9e5f6", 0.1),
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    // Inter font not bundled; falls back to system UI fonts.
    fontFamily:
      '"Inter Variable", "Inter", "Aptos", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontSize: "2.5rem",
      fontWeight: 800,
      letterSpacing: "-0.04em",
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 800,
      letterSpacing: "-0.035em",
    },
    h3: {
      fontSize: "1.75rem",
      fontWeight: 800,
      letterSpacing: "-0.03em",
    },
    h4: {
      // Large metric values and prominent panel titles.
      fontSize: "1.5rem",
      fontWeight: 700,
      letterSpacing: "-0.025em",
      // Tabular numerics for aligned metric displays.
      fontVariantNumeric: "tabular-nums",
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 800,
      letterSpacing: "-0.025em",
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 700,
      letterSpacing: "-0.01em",
    },
    subtitle1: {
      fontSize: "0.9375rem",
      fontWeight: 500,
      letterSpacing: "-0.01em",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      letterSpacing: "-0.005em",
    },
    body1: {
      fontSize: "0.9375rem",
      lineHeight: 1.6,
    },
    body2: {
      fontSize: "0.8125rem",
      lineHeight: 1.55,
    },
    caption: {
      // Compact metadata labels and secondary annotations.
      fontSize: "0.6875rem",
      fontWeight: 500,
      letterSpacing: "0.03em",
    },
    overline: {
      // Uppercase panel category labels, e.g. "POPULATION BY ZONE".
      fontSize: "0.625rem",
      fontWeight: 700,
      letterSpacing: "0.1em",
      lineHeight: 1,
    },
    button: {
      fontWeight: 800,
      textTransform: "none",
      letterSpacing: "0.01em",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "html, body, #root": {
          maxWidth: "100%",
          overflowX: "hidden",
        },
        body: {
          minWidth: 320,
          minHeight: "100vh",
          background:
            "radial-gradient(circle at 50% -10%, rgba(32, 93, 156, 0.22), transparent 42%), #041c39",
        },
        "#root": {
          minHeight: "100vh",
        },
      },
    },
    // Suppress MUI's dark-mode paper gradient across all surfaces.
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          minHeight: 42,
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&.Mui-selected": {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
          },
          "&.Mui-selected:hover": {
            backgroundColor: theme.palette.primary.dark,
            borderColor: theme.palette.primary.dark,
          },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 5,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
          boxShadow: "none",
        }),
      },
    },
    MuiChip: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          fontWeight: 700,
          letterSpacing: "0.04em",
          borderRadius: 4,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 4,
          borderRadius: 2,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
    },
    MuiSelect: {
      defaultProps: {
        size: "small",
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: alpha(theme.palette.common.black, 0.08),
          borderRadius: theme.shape.borderRadius,
        }),
        notchedOutline: ({ theme }) => ({
          borderColor: alpha(theme.palette.common.white, 0.1),
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          minHeight: 36,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: alpha(theme.palette.common.white, 0.08),
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
          fontSize: "0.75rem",
          fontWeight: 500,
          color: theme.palette.text.primary,
          borderRadius: 5,
        }),
        arrow: ({ theme }) => ({
          color: theme.palette.background.paper,
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
        }),
      },
    },
  },
});
